import { getLocationId } from 'helpers/session';
import AccountStatementService from 'root/apps/QuoteMachineIntegration/services/AccountStatementService';
import AutomatedAccountReceivablesService from 'root/apps/QuoteMachineIntegration/services/AutomatedAccountReceivablesService';
import CustomerAccountService from 'root/apps/QuoteMachineIntegration/services/CustomerAccountService';
import CustomerService from 'root/apps/QuoteMachineIntegration/services/CustomerService';
import InvoiceService from 'root/apps/QuoteMachineIntegration/services/InvoiceService';
import LocationService from 'root/apps/QuoteMachineIntegration/services/LocationService';
import { AccountStatementOption } from 'root/apps/QuoteMachineIntegration/state/AccountStatementOption';
import { AccountStatementSendPayload } from 'root/apps/QuoteMachineIntegration/state/AccountStatementSendPayload';
import InvoicingCustomer from 'root/apps/QuoteMachineIntegration/state/Customer';
import Location from 'root/apps/QuoteMachineIntegration/state/Location';
import { handleClickOnAutomaticARRecipient } from 'root/apps/QuoteMachineIntegration/utils/AutomaticARRecipientRedirect';
import { getProposalPreviewURL } from 'root/apps/QuoteMachineIntegration/utils/ProposalPreviewConfig';
import { datadogRum } from 'root/datadog/datadogRum';

import {
  ToastNotificationAction,
  ToastNotificationTypes,
} from '../CustomerCreditAccountDetails/CustomerCreditAccountDetailsHeading';

import type {
  EditCreditLimitActionData,
  InvoicingActionDetail,
  InvoicingErrorDetail,
  RequestStatementPreviewActionData,
  SendAccountStatementActionData,
  UpdateAutomatedArStatusActionData,
  ViewAutoArRecipientActionData,
  ViewInvoiceActionData,
  ViewTransactionActionData,
} from './types';

/**
 * Narrows the generic `invoicing:action` payload to a specific action's data
 * shape. The bridge through `unknown` is intentional: `data` arrives off an
 * untyped CustomEvent, and the per-action shapes are defined in `./types`.
 */
function asActionData<T>(data: Record<string, unknown>): T {
  return data as unknown as T;
}

const EVENT_ACTION = 'invoicing:action';
const EVENT_ERROR = 'invoicing:error';

export interface InvoicingActionRouterContext {
  accountID: string;
  customerID: string;
  creditAccountID: string | number;
  autoArError?: boolean;
}

function dispatchActionResponse(
  element: HTMLElement,
  actionName: string,
  requestId: string,
  result: unknown
): void {
  element.dispatchEvent(
    new CustomEvent(`invoicing:${actionName}-response`, {
      detail: { requestId, result },
      bubbles: true,
      composed: true,
    })
  );
}

function dispatchActionError(
  element: HTMLElement,
  actionName: string,
  requestId: string,
  error: string
): void {
  element.dispatchEvent(
    new CustomEvent(`invoicing:${actionName}-response`, {
      detail: { requestId, error },
      bubbles: true,
      composed: true,
    })
  );
}

async function handleEditCreditLimit(customerID: string, amount: string): Promise<void> {
  const data = await CustomerAccountService.createOrUpdateCustomerCreditAccount(customerID, amount);
  if (data) {
    window.location.reload();
  }
}

async function handleRequestStatementPreview(
  context: InvoicingActionRouterContext,
  data: RequestStatementPreviewActionData
): Promise<{ url: string; accountRef: string; objectData: Record<string, unknown> }> {
  const { creditAccountDetails } = data;
  const locationId = getLocationId();
  const locationData = await LocationService.getLocation(locationId);
  const location = new Location(locationData);
  const customerData = await CustomerService.getCustomer(context.customerID);
  const customerDetails = new InvoicingCustomer(customerData);
  const invoicingCreditAccountId = creditAccountDetails?.invoicingContactId;

  if (!invoicingCreditAccountId || invoicingCreditAccountId === 'undefined') {
    throw new Error('Invoicing credit account ID is required for account statement preview');
  }

  return {
    url: getProposalPreviewURL(),
    accountRef: context.accountID != null ? String(context.accountID) : '',
    objectData: {
      creditAccountID: invoicingCreditAccountId,
      integrationKey: 'ls-retail',
      location,
      customerDetails,
    },
  };
}

async function handleSendAccountStatement(
  context: InvoicingActionRouterContext,
  data: SendAccountStatementActionData
): Promise<{ type: string; action: string; accountStatementIdentifier?: string }> {
  const { formData } = data;

  if (formData.option !== AccountStatementOption.SEND) {
    return {
      type: ToastNotificationTypes.NEGATIVE,
      action: ToastNotificationAction.SEND_STATEMENT_ERROR,
    };
  }

  const payload: AccountStatementSendPayload = {
    emailMethod: {
      recipientAddresses: [formData.email],
    },
    ...(formData.invoicesDueDateRange
      ? { invoicesDueDateRange: formData.invoicesDueDateRange }
      : {}),
  };

  if (!context.autoArError) {
    AutomatedAccountReceivablesService.processAutoArOptions(
      formData.automatedAccountReceivablesOptions,
      context.creditAccountID.toString()
    );
  }

  try {
    const { status, statementIdentifier } =
      await AccountStatementService.createAndSendAccountStatement(
        context.creditAccountID.toString(),
        context.customerID,
        payload
      );

    if (status === 202) {
      return {
        type: ToastNotificationTypes.SUCCESS,
        action: ToastNotificationAction.SEND_STATEMENT_SUCCESS,
        accountStatementIdentifier: statementIdentifier,
      };
    }

    return {
      type: ToastNotificationTypes.NEGATIVE,
      action: ToastNotificationAction.SEND_STATEMENT_ERROR,
    };
  } catch {
    return {
      type: ToastNotificationTypes.NEGATIVE,
      action: ToastNotificationAction.SEND_STATEMENT_ERROR,
    };
  }
}

function handleViewTransaction(transactionRef: string): void {
  if (!transactionRef) return;

  const transactionId = transactionRef.replace(/^ls-retail_/, '');
  if (!transactionId) return;

  window.showNotifier('working');
  const transactionUrl = `${window.location.origin}/?name=transaction.views.transaction&form_name=view&id=${transactionId}&tab=details`;
  window.open(transactionUrl, '_blank', 'noopener,noreferrer');
  window.hideNotifier('working');
}

async function handleViewInvoice(proposalLink: string, invoiceId: string): Promise<void> {
  if (!proposalLink || !invoiceId) return;

  try {
    const merchantToken = await InvoiceService.getProposalToken(invoiceId);
    const url = merchantToken
      ? `${proposalLink}?merchantToken=${encodeURIComponent(merchantToken)}`
      : proposalLink;
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    datadogRum.addError(error, {
      message: `Failed to get merchant token for invoice ${invoiceId}`,
      invoiceId,
      proposalLink,
    });
    window.open(proposalLink, '_blank', 'noopener,noreferrer');
  }
}

export function createInvoicingActionHandler(
  element: HTMLElement,
  context: InvoicingActionRouterContext
): EventListener {
  return (event: Event) => {
    const { action, data = {} } = (event as CustomEvent<InvoicingActionDetail>).detail;
    const requestId = typeof data.requestId === 'string' ? data.requestId : undefined;

    const runAsync = async () => {
      switch (action) {
        case 'edit-credit-limit': {
          const payload = asActionData<EditCreditLimitActionData>(data);
          await handleEditCreditLimit(payload.customerId ?? context.customerID, payload.amount);
          break;
        }
        case 'request-statement-preview':
          if (!requestId) return;
          try {
            const result = await handleRequestStatementPreview(
              context,
              asActionData<RequestStatementPreviewActionData>(data)
            );
            dispatchActionResponse(element, action, requestId, result);
          } catch (error) {
            dispatchActionError(
              element,
              action,
              requestId,
              error instanceof Error ? error.message : 'Statement preview failed'
            );
          }
          break;
        case 'send-account-statement':
          if (!requestId) return;
          try {
            const result = await handleSendAccountStatement(
              context,
              asActionData<SendAccountStatementActionData>(data)
            );
            dispatchActionResponse(element, action, requestId, result);
          } catch (error) {
            dispatchActionError(
              element,
              action,
              requestId,
              error instanceof Error ? error.message : 'Send statement failed'
            );
          }
          break;
        case 'fetch-automated-ar-actions':
          // No payload fields are read; the credit account comes from context.
          if (!requestId) return;
          try {
            const result = await AutomatedAccountReceivablesService.fetchActions(
              context.creditAccountID.toString()
            );
            dispatchActionResponse(element, action, requestId, result);
          } catch (error) {
            dispatchActionError(
              element,
              action,
              requestId,
              error instanceof Error ? error.message : 'Failed to fetch Auto AR actions'
            );
          }
          break;
        case 'update-automated-ar-status': {
          const payload = asActionData<UpdateAutomatedArStatusActionData>(data);
          await AutomatedAccountReceivablesService.updateAutomatedAccountReceivablesStatus(
            payload.context,
            payload.automationEnabled,
            payload.creditAccountId ?? context.creditAccountID.toString()
          );
          break;
        }
        case 'view-auto-ar-recipient': {
          const payload = asActionData<ViewAutoArRecipientActionData>(data);
          handleClickOnAutomaticARRecipient(payload.recipientId, 'details', false);
          break;
        }
        case 'view-invoice': {
          const payload = asActionData<ViewInvoiceActionData>(data);
          await handleViewInvoice(payload.proposalLink, payload.invoiceId);
          break;
        }
        case 'view-transaction':
        case 'view-deposit': {
          const payload = asActionData<ViewTransactionActionData>(data);
          handleViewTransaction(payload.transactionRef);
          break;
        }
        default:
          break;
      }
    };

    runAsync().catch((error) => {
      datadogRum.addError(error, {
        source: 'customer-profile-web-components',
        action,
      });
      if (requestId) {
        dispatchActionError(
          element,
          action,
          requestId,
          error instanceof Error ? error.message : 'Action failed'
        );
      }
    });
  };
}

export function createInvoicingErrorHandler(): EventListener {
  return (event: Event) => {
    const detail = (event as CustomEvent<InvoicingErrorDetail>).detail;
    const error = new Error(detail?.message ?? 'invoicing customer profile web component error');
    if (detail?.stack) error.stack = detail.stack;
    datadogRum.addError(error, {
      componentStack: detail?.componentStack,
      source: 'customer-profile-web-components',
    });
  };
}

export const INVOICING_ACTION_EVENT = EVENT_ACTION;
export const INVOICING_ERROR_EVENT = EVENT_ERROR;
